import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import { FormattedMessage } from 'react-intl'
import LazyHydrate from 'react-lazy-hydration'

import { useMediaQuery, Theme } from '@mui/material'

import Container from 'app/components/base/Container'
import GridList from 'app/components/base/GridList'
import Text from 'app/components/base/Text'
import ArticleCard from 'app/features/Blog/components/ArticleCard'
import ArticlesPanel from 'app/features/Blog/components/ArticlesPanel'
import BackgroundContainer from 'app/features/Blog/components/BackgroundContainer'
import FeaturedArticles from 'app/features/Blog/FeaturedArticles'
import TagPanel from 'app/features/Blog/TagPanel'
import { generateArticleLink } from 'app/utils/helpers/blog.helpers'

import ArticlesTabs from '~/components/common/ArticleTabs'
import NextSeo from '~/containers/NextSeo'

import { useBlogView } from './hooks/useBlogView'

const EmptyBlog = dynamic(
  () => import('app/features/Blog/components/EmptyBlog'),
  { ssr: false }
)
const Pagination = dynamic(() => import('app/components/base/Pagination'), {
  ssr: false
})

const BlogView = () => {
  const router = useRouter()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const {
    articles,
    articlesPanelRef,
    handleArticleTagClick,
    hasArticlesInCategory,
    isEmptyBlog,
    isEmptyCategory,
    isPageLoading,
    page,
    pagy,
    setPage,
    showPagination,
    metaTitle,
    metaDescription
  } = useBlogView()

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        openGraph={{ title: metaTitle, description: metaDescription }}
      />

      <BackgroundContainer
        sx={{
          ...(isEmptyBlog && {
            backgroundColor: 'background.default',
            '& > #icons': { display: 'none' }
          })
        }}
      >
        <Text
          variant="h1"
          typography={{ xs: 'h2', lg: 'h1' }}
          sx={{ textAlign: 'center', mb: 1 }}
        >
          <FormattedMessage defaultMessage="Auto Insights" />
        </Text>

        <Text
          variant="body1"
          sx={{
            textAlign: 'center',
            mb: 4,
            maxWidth: '500px',
            mx: 'auto'
          }}
        >
          <FormattedMessage defaultMessage="Rev up your auto know-how and unleash the gearhead within! Master ifndautoparts, expert industry tips, platform updates and latest car news" />
        </Text>

        <FeaturedArticles isLoading={router.isFallback} />

        {isEmptyBlog && <EmptyBlog />}
      </BackgroundContainer>

      {!isEmptyBlog && (
        <Container
          sx={{ maxWidth: { xs: '920px' }, pb: { xs: '56px', md: '86px' } }}
        >
          <ArticlesPanel ref={articlesPanelRef}>
            <LazyHydrate whenVisible>
              <>
                <ArticlesTabs
                  isMobile={isMobile}
                  sx={{ mb: { xs: 0, md: 3 } }}
                />

                <TagPanel
                  isMobile={isMobile}
                  withIcon
                  onTagClick={handleArticleTagClick}
                  sx={{ mt: { xs: 2, md: 3 }, mb: { xs: 4, md: 5 } }}
                />

                {isEmptyCategory && <EmptyBlog />}
              </>
            </LazyHydrate>

            {hasArticlesInCategory && (
              <GridList
                items={articles}
                columns={{ xs: 1, sm: 2, md: 3 }}
                spacing={4}
                keyExtractor={({ id }) => id}
                renderItem={(item: BlogArticleModel, index) => {
                  const link = generateArticleLink({
                    slug: item.slug,
                    categorySlug: item.blog_category?.slug
                  })

                  return (
                    <LazyHydrate whenVisible>
                      <ArticleCard
                        loading={isPageLoading}
                        link={link}
                        image={item.title_image_url}
                        title={item.title}
                        createdAt={item.created_at}
                        index={index}
                        isMobile={isMobile}
                        imageAltText={item?.title_image?.alt_text}
                      />
                    </LazyHydrate>
                  )
                }}
              />
            )}

            {showPagination && (
              <LazyHydrate whenVisible>
                <Pagination
                  onChange={setPage}
                  count={pagy?.pages}
                  page={page}
                  siblingCount={1}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: 5
                  }}
                />
              </LazyHydrate>
            )}
          </ArticlesPanel>
        </Container>
      )}
    </>
  )
}

export default BlogView
